import { useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  apiErrorStatus,
  apiErrorText,
  apiLoadErrorText,
  prcVersionArg,
  useGetPrcLimitsQuery,
  useLazyGetPrcVersionQuery,
  useSavePrcLimitsMutation,
  type PrcVersionPayload,
} from '@shared/api';
import { pluralizeRu } from '@shared/lib/formatFile';
import { Button } from '@shared/ui/Button';
import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerFooter,
  DrawerHeader,
} from '@shared/ui/Drawer';
import { SkeletonLine } from '@shared/ui/Skeleton';
import {
  NUMBER_HINT,
  ScrollRegion,
  VisuallyHidden,
  normalizeDecimal,
  typo,
} from '@entities/fleet';
import {
  applyLimitEdits,
  compareDecimal,
  isEditableStatus,
  limitCellKey,
  limitRows,
  limitsEvaluation,
  limitsEvaluationError,
  percentChange,
  type LimitCellView,
  type LimitEdit,
} from '@entities/prc';
import { Actions, FooterStatus, Notice, TextButton } from '@widgets/FleetRegistry/ui';
import type { PrcAccess } from '../../access';
import { DrawerTitle, Eyebrow, HeaderMeta, HeaderRow, Titles } from '../../drawerParts';
import { formatPercent, formatRub, formatServerMoney } from '../../format';
import { SchemeScope, Stack, StatGrid } from '../../ui';
import {
  BASIS,
  CellNote,
  ErrorList,
  Table,
  TableNote,
  cellText,
  inputOf,
} from './styles';
import { ResourceCell } from './ResourceCell';
import { EditCell } from './EditCell';

/**
 * Перечень лимитов на ремонт модели: строки запчастей и работ, количество в
 * год и цена правятся (число вместо формулы или вернуть формулу книги);
 * итог и норма — по ответу сервера после сохранения: формулы книги на
 * клиенте не вычисляются.
 */
export function LimitsDrawer({
  payload,
  access,
  modelId,
  onClose,
}: {
  payload: PrcVersionPayload;
  access: PrcAccess;
  modelId: string;
  onClose: () => void;
}) {
  const uid = useId();
  const titleRef = useRef<HTMLHeadingElement>(null);
  const v = payload.version;
  const { data: detail, error, isFetching, refetch } = useGetPrcLimitsQuery({ id: v.id, modelId });
  const [save, saveState] = useSavePrcLimitsMutation();
  const [fetchVersion] = useLazyGetPrcVersionQuery();
  const [inputs, setInputs] = useState<Record<string, string>>({});
  const [restores, setRestores] = useState<ReadonlySet<string>>(() => new Set());
  const [showErrors, setShowErrors] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  const current = detail && detail.model_id === modelId ? detail : undefined;
  const block = current?.block ?? null;
  const evaluation = limitsEvaluation(current?.evaluation ?? null);
  const evalError = limitsEvaluationError(current?.evaluation ?? null);
  const rows = useMemo(() => (block ? limitRows(block, evaluation) : []), [block, evaluation]);
  const editable =
    access.canWrite && isEditableStatus(current?.status ?? v.status) && block !== null;
  const modelRow = payload.models.find((m) => m.model_id === modelId);
  const name = current?.model_name ?? modelRow?.model_name ?? 'Модель';

  // Новый ответ сервера после сохранения — поля от него. После 409 правки
  // остаются в полях: человек проверит их на свежем перечне.
  const conflictRef = useRef(conflict);
  useEffect(() => {
    conflictRef.current = conflict;
  }, [conflict]);
  useEffect(() => {
    if (conflictRef.current) return;
    setInputs({});
    setRestores(new Set());
  }, [current?.updated_at, modelId]);

  const viewByKey = useMemo(() => {
    const map = new Map<string, LimitCellView>();
    for (const r of rows) {
      map.set(limitCellKey(r.row, 'F'), r.qty);
      map.set(limitCellKey(r.row, 'G'), r.price);
    }
    return map;
  }, [rows]);

  const { edits, issues } = useMemo(() => {
    const out: Record<string, LimitEdit> = {};
    const bad = new Map<string, string>();
    for (const [key, raw] of Object.entries(inputs)) {
      const cell = viewByKey.get(key);
      if (!cell || raw === inputOf(cell)) continue;
      const n = normalizeDecimal(raw);
      if (n === null) bad.set(key, raw.trim() === '' ? 'Укажите число' : NUMBER_HINT);
      else out[key] = { kind: 'value', value: n };
    }
    for (const key of restores) out[key] = { kind: 'formula' };
    return { edits: out, issues: bad };
  }, [inputs, restores, viewByKey]);
  const dirty = Object.keys(edits).length + issues.size;

  const confirmClose = () =>
    dirty === 0 ||
    window.confirm(
      `Закрыть без сохранения? Правки в ${dirty} ${pluralizeRu(dirty, ['ячейке', 'ячейках', 'ячейках'])} пропадут.`,
    );

  function close() {
    if (confirmClose()) onClose();
  }

  async function submit(nextBlock: typeof block) {
    if (!current) return;
    setServerError(null);
    setConflict(null);
    const before = modelRow;
    try {
      const res = await save({
        id: v.id,
        modelId,
        block: nextBlock,
        expected_updated_at: current.updated_at,
      }).unwrap();
      const after = res.models.find((m) => m.model_id === modelId);
      const parts: string[] = [
        nextBlock ? 'Перечень сохранён' : 'Перечень убран — норма ремонта из паспорта',
      ];
      const nb = before?.repair_limits?.norm_pct;
      const na = after?.repair_limits?.norm_pct;
      if (nextBlock && na) {
        parts.push(
          nb && compareDecimal(nb, na) !== 0
            ? `норма ремонта по перечню ${formatPercent(nb, 2)} → ${formatPercent(na, 2)}`
            : `норма ремонта по перечню ${formatPercent(na, 2)}`,
        );
      }
      const pb = before?.figures.price;
      const pa = after?.figures.price;
      if (pb && pa) {
        const pct = percentChange(pb, pa, 2);
        parts.push(
          compareDecimal(pb, pa) === 0
            ? `цена без НДС не изменилась: ${formatRub(pa)}`
            : `цена без НДС ${formatRub(pb)} → ${formatRub(pa)}${pct ? ` (${formatPercent(pct, 2, true)})` : ''}`,
        );
      }
      setResult(`${parts.join('; ')}.`);
      setShowErrors(false);
      setConfirmRemove(false);
    } catch (err) {
      if (apiErrorStatus(err) === 409) {
        const fresh = await fetchVersion(prcVersionArg(v.id))
          .unwrap()
          .catch(() => null);
        if (fresh && isEditableStatus(fresh.version.status)) {
          const message =
            apiErrorText(err, 'Версию уже изменил кто-то другой.') +
            ' Перечень перечитан, ваши правки остались в полях — проверьте и сохраните ещё раз.';
          // До перечитывания: свежий ответ не должен стереть правки в полях.
          conflictRef.current = message;
          setConflict(message);
          await refetch();
          return;
        }
      }
      setServerError(apiErrorText(err, 'Не удалось сохранить перечень.'));
    }
  }

  function onSave() {
    if (!block) return;
    if (issues.size > 0) {
      setShowErrors(true);
      return;
    }
    void submit(applyLimitEdits(block, edits));
  }

  const footer = saveState.isLoading
    ? 'Сохраняю и пересчитываю…'
    : showErrors && issues.size > 0
      ? `Проверьте ${issues.size} ${pluralizeRu(issues.size, ['значение', 'значения', 'значений'])}`
      : dirty > 0
        ? `Изменено ${dirty} ${pluralizeRu(dirty, ['ячейка', 'ячейки', 'ячеек'])} — итог и норму пересчитает сервер`
        : '';

  return (
    <Drawer open onClose={close} labelledBy={`${uid}-title`} size="lg" initialFocusRef={titleRef}>
      <SchemeScope>
        <DrawerHeader>
          <HeaderRow>
            <Titles>
              <Eyebrow>Лимиты на ремонт · перечень запчастей и работ</Eyebrow>
              <DrawerTitle id={`${uid}-title`} ref={titleRef} tabIndex={-1}>
                {name}
              </DrawerTitle>
              <HeaderMeta>
                <span className="text">
                  {block?.basis ? (BASIS[block.basis] ?? block.basis) : 'Перечня нет'}
                  {block ? ` · строк ${rows.length}` : ''}
                </span>
              </HeaderMeta>
            </Titles>
            <DrawerCloseButton onClick={close} />
          </HeaderRow>
        </DrawerHeader>

        {error !== undefined && !current ? (
          <DrawerBody>
            <Notice $tone="danger" role="alert">
              <span className="text">
                {apiLoadErrorText(
                  error,
                  'Не удалось открыть перечень лимитов.',
                  'Нет доступа к ПРЦ.',
                )}
              </span>
            </Notice>
          </DrawerBody>
        ) : !current ? (
          <DrawerBody aria-busy="true">
            <SkeletonLine $height={64} />
            <SkeletonLine $height={240} />
          </DrawerBody>
        ) : (
          <DrawerBody aria-busy={isFetching}>
            <Stack>
              {result && (
                <Notice $tone="success" role="status">
                  <span className="text">{typo(result)}</span>
                  <TextButton type="button" onClick={() => setResult(null)}>
                    Скрыть
                  </TextButton>
                </Notice>
              )}
              {conflict && (
                <Notice $tone="warning" role="alert">
                  <span className="text">{typo(conflict)}</span>
                </Notice>
              )}
              {serverError && (
                <Notice $tone="danger" role="alert">
                  <span className="text">{serverError}</span>
                </Notice>
              )}
              {evalError && (
                <Notice $tone="warning">
                  <span className="text">
                    {typo(`Перечень не считается: ${evalError}. Норма ремонта взята из паспорта.`)}
                  </span>
                </Notice>
              )}
              {evaluation && evaluation.errors.length > 0 && (
                <Notice $tone="warning">
                  <span className="text">
                    {typo(
                      `Ошибки в формулах перечня (${evaluation.errors.length}) — ячейка считается нулём:`,
                    )}
                  </span>
                  <ErrorList>
                    {evaluation.errors.slice(0, 12).map((e) => (
                      <li key={`${e.row}-${e.col}`}>
                        строка {e.row}, колонка {e.col}: {e.error}
                      </li>
                    ))}
                  </ErrorList>
                </Notice>
              )}
            </Stack>

            {!block ? (
              <Notice $tone="info">
                <span className="text">
                  {typo(
                    `У модели нет перечня лимитов: норма ремонта и ТО — из паспорта (${formatPercent(current.passport_norm_pct, 2)}). Перечень приходит с книгой ЭММ.`,
                  )}
                </span>
              </Notice>
            ) : (
              <>
                <StatGrid $cols={3} aria-label="Итог перечня">
                  <div>
                    <dt>Норма по перечню</dt>
                    <dd className="big">
                      {evaluation ? formatPercent(evaluation.norm_pct, 2) : '—'}
                    </dd>
                  </div>
                  <div>
                    <dt>Норма в паспорте</dt>
                    <dd className="big">{formatPercent(current.passport_norm_pct, 2)}</dd>
                  </div>
                  <div>
                    <dt>Итог перечня в год</dt>
                    <dd className="big">
                      {evaluation ? formatServerMoney(evaluation.total) : '—'}
                    </dd>
                  </div>
                </StatGrid>

                <TableNote>
                  {typo(
                    editable
                      ? 'ƒ — количество или цена считается формулой книги. Введите число — оно заменит формулу; формулу книги можно вернуть. Итог и норму пересчитает сервер при сохранении.'
                      : 'ƒ — количество или цена считается формулой книги.',
                  )}
                </TableNote>
                <ScrollRegion label={`Перечень лимитов: ${name}`}>
                  <Table>
                    <caption>
                      <VisuallyHidden>
                        Строки перечня: ресурс, лимит, количество в год, цена и стоимость, ₽ без НДС
                      </VisuallyHidden>
                    </caption>
                    <thead>
                      <tr>
                        <th scope="col">№</th>
                        <th scope="col">Запчасть или работа</th>
                        <th scope="col">Ресурс</th>
                        <th scope="col">Лимит</th>
                        <th scope="col" className="num">
                          Кол-во в год
                        </th>
                        <th scope="col" className="num">
                          Цена, ₽
                        </th>
                        <th scope="col" className="num">
                          Стоимость в год, ₽
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => {
                        const keyF = limitCellKey(r.row, 'F');
                        const keyG = limitCellKey(r.row, 'G');
                        const pendingRow = keyF in edits || keyG in edits;
                        return (
                          <tr key={r.row} className={r.kind === 'item' ? 'row' : r.kind}>
                            <td className="row-no">{r.row}</td>
                            <td className={`c-name${r.kind === 'total' ? ' strong' : ''}`}>
                              {r.name || '—'}
                            </td>
                            <td className="c-res">
                              {r.kind === 'item' ? <ResourceCell row={r} /> : ''}
                            </td>
                            <td className="c-text">
                              {r.kind === 'item' ? cellText(r.limit, 2) : ''}
                            </td>
                            <td className="cell num">
                              {r.kind === 'total' ? null : (
                                <>
                                  <EditCell
                                    row={r}
                                    col="F"
                                    cell={r.qty}
                                    editable={editable}
                                    value={inputs[keyF]}
                                    pendingFormula={restores.has(keyF)}
                                    error={showErrors ? (issues.get(keyF) ?? null) : null}
                                    label={
                                      r.kind === 'percent'
                                        ? `${r.name}: процент от суммы строк выше`
                                        : `${r.name}: количество в год`
                                    }
                                    idPrefix={uid}
                                    onChange={(value) =>
                                      setInputs((p) => ({ ...p, [keyF]: value }))
                                    }
                                    onRestore={() => setRestores((p) => new Set(p).add(keyF))}
                                    onUndo={() =>
                                      setRestores((p) => {
                                        const n = new Set(p);
                                        n.delete(keyF);
                                        return n;
                                      })
                                    }
                                  />
                                  {r.kind === 'percent' && <CellNote>% от суммы выше</CellNote>}
                                </>
                              )}
                            </td>
                            <td className="cell num">
                              {r.kind === 'item' ? (
                                <EditCell
                                  row={r}
                                  col="G"
                                  cell={r.price}
                                  editable={editable}
                                  value={inputs[keyG]}
                                  pendingFormula={restores.has(keyG)}
                                  error={showErrors ? (issues.get(keyG) ?? null) : null}
                                  label={`${r.name}: цена, ₽ без НДС`}
                                  idPrefix={uid}
                                  onChange={(value) => setInputs((p) => ({ ...p, [keyG]: value }))}
                                  onRestore={() => setRestores((p) => new Set(p).add(keyG))}
                                  onUndo={() =>
                                    setRestores((p) => {
                                      const n = new Set(p);
                                      n.delete(keyG);
                                      return n;
                                    })
                                  }
                                />
                              ) : null}
                            </td>
                            <td className={`num${r.kind === 'total' ? ' strong' : ''}`}>
                              {pendingRow ? (
                                <span className="pending" title={`Было: ${cellText(r.cost, 2)}`}>
                                  пересчитается
                                </span>
                              ) : (
                                cellText(r.cost, 2)
                              )}
                              {r.cost.error && <CellNote>{r.cost.error}</CellNote>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </ScrollRegion>
              </>
            )}
          </DrawerBody>
        )}

        {editable && (
          <DrawerFooter>
            {confirmRemove ? (
              <>
                <FooterStatus role="alert">
                  {typo('Убрать перечень? Норма ремонта модели снова будет из паспорта.')}
                </FooterStatus>
                <Actions>
                  <Button
                    type="button"
                    $variant="danger"
                    disabled={saveState.isLoading}
                    onClick={() => void submit(null)}
                  >
                    {saveState.isLoading ? 'Убираю…' : 'Убрать перечень'}
                  </Button>
                  <Button type="button" $variant="ghost" onClick={() => setConfirmRemove(false)}>
                    Отмена
                  </Button>
                </Actions>
              </>
            ) : (
              <>
                <Button
                  type="button"
                  $variant="ghost"
                  disabled={saveState.isLoading || dirty > 0}
                  title={dirty > 0 ? 'Сначала сохраните или отмените правки' : undefined}
                  onClick={() => setConfirmRemove(true)}
                >
                  Убрать перечень (норма из паспорта)
                </Button>
                <FooterStatus role="status">{footer}</FooterStatus>
                <Actions>
                  <Button
                    type="button"
                    $variant="ghost"
                    disabled={saveState.isLoading || dirty === 0}
                    onClick={() => {
                      setInputs({});
                      setRestores(new Set());
                      setShowErrors(false);
                    }}
                  >
                    Отменить правки
                  </Button>
                  <Button
                    type="button"
                    $variant="primary"
                    disabled={saveState.isLoading || dirty === 0}
                    onClick={onSave}
                  >
                    {saveState.isLoading ? 'Сохраняю…' : 'Сохранить и пересчитать'}
                  </Button>
                </Actions>
              </>
            )}
          </DrawerFooter>
        )}
      </SchemeScope>
    </Drawer>
  );
}
